import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiFout, api } from '@/lib/api';
import type { PraktijkProfiel, SessieAntwoord, SipInstellingen } from '@/lib/api-types';
import { DEFAULT_PRACTICE_SETTINGS, PracticeSettings } from '@/types/practice-settings';

/**
 * De sessie van een zorgverlener: één tokenruil, daarna een cookie.
 *
 * Wat hier verdwijnt is een patroon dat door de hele frontend liep. Elke
 * edge-function-aanroep stuurde `token`, `user_id` en `app_token` mee in zijn body,
 * dus die drie werden als props doorgegeven aan elk component dat iets met de server
 * deed -- en ze stonden in de URL van /settings en /handleiding, en dus in de
 * browsergeschiedenis. Nu wisselt deze hook ze één keer in voor een HttpOnly-cookie,
 * en weet de rest van de applicatie niet meer dat er tokens bestaan.
 *
 * De vorige versie deed de tokenruil met een handmatige `fetch` in plaats van via de
 * Supabase-client, met een comment erbij: die client wachtte op `getSession()` en kon
 * daarbij een synchrone tokenvernieuwing uitlokken, wat 200 tot 800 ms aan het
 * kritieke "Toegang controleren"-pad toevoegde. Dat probleem bestaat niet meer -- er
 * is geen client die eerst iets anders moet doen.
 */

export type SessieStatus =
  | { fase: 'bezig' }
  /** Er is een geldige sessie. */
  | { fase: 'ok' }
  /** Er is geen token in de URL, of het token is ongeldig. */
  | { fase: 'geen-toegang'; melding: string }
  /** Wel een token en een gebruiker, maar geen app_token: vraag erom. */
  | { fase: 'app-token-nodig' }
  /** De praktijk of gebruiker is gedeactiveerd. */
  | { fase: 'geblokkeerd'; melding: string };

export interface PracticeSession {
  status: SessieStatus;
  practice: PraktijkProfiel | null;
  practiceUserId: string | null;
  sip: SipInstellingen | null;
  settings: PracticeSettings;
  /** True als de tokenruil deze praktijk zojuist heeft aangemaakt. */
  practiceIsNew: boolean;
  /**
   * True zolang de praktijknaam nog niet is ingevuld. Anders dan practiceIsNew
   * blijft dit waar na een herlaadactie, zodat de wizard de naam blijft vragen.
   */
  practiceNeedsName: boolean;
  /** True als deze gebruiker nog geen profiel heeft ingevuld. */
  needsOnboarding: boolean;
  /** Handmatig een app-token proberen, wanneer het HIS er geen meestuurde. */
  probeerAppToken: (appToken: string) => Promise<void>;
  /** Na een wijziging in de eigen voorkeuren: de gegevens opnieuw ophalen. */
  herlaad: () => Promise<void>;
  /** Lokaal bijwerken zonder een rondje naar de server, na een geslaagde PATCH. */
  pasSettingsAan: (deel: Partial<PracticeSettings>) => void;
  markeerOnboardingKlaar: () => void;
  markeerPraktijkNaam: (naam: string) => void;
}

/**
 * Zet het antwoord van de API om in de instellingen die het opnamescherm gebruikt.
 *
 * De gebruikersvoorkeur wint boven de praktijkinstelling; dat was ook de regel in de
 * bron, maar daar stond hij uitgeschreven in SoepPage.tsx als veertig regels
 * `??`-ketens. Hier staat hij één keer.
 */
function naarSettings(antwoord: SessieAntwoord): PracticeSettings {
  const p = antwoord.practice;
  const u = antwoord.user;
  const sip = antwoord.sip;

  /** Leeg telt als niet gezet, net als op de server. */
  const tekst = (van: string | null | undefined, terug: string): string =>
    van !== null && van !== undefined && van !== '' ? van : terug;

  return {
    auto_translate: u?.autoTranslate ?? p.autoTranslate,
    auto_suggest_icpc: u?.autoSuggestIcpc ?? p.autoSuggestIcpc,
    doctor_phrase: tekst(u?.doctorPhrase, p.doctorPhrase),
    read_trigger_phrase: tekst(u?.readTriggerPhrase, p.readTriggerPhrase),
    provider_role: u?.providerRole ?? '',
    provider_specializations: u?.providerSpecializations ?? [],
    report_verbosity: u?.reportVerbosity ?? 'gemiddeld',
    personal_profile: u?.personalProfile ?? '',
    force_single_soep: u?.forceSingleSoep ?? false,

    local_api_url: tekst(p.localApiUrl, DEFAULT_PRACTICE_SETTINGS.local_api_url),
    default_languages: p.defaultLanguages.length > 0 ? p.defaultLanguages : ['nl-NL'],
    calling_enabled: p.callingEnabled,
    patient_share_enabled: p.patientShareEnabled,
    live_transcript_enabled: p.liveTranscriptEnabled,

    // SIP komt al samengevoegd van de server; 8089 is de standaardpoort en hoort
    // niet in de URL terecht te komen.
    sip_host: sip?.host ?? '',
    sip_ws_port: sip?.wsPort === '8089' ? '' : (sip?.wsPort ?? ''),
    sip_ws_path: tekst(sip?.wsPath, '/ws'),
    sip_extension: sip?.extension ?? '',
    sip_password: sip?.password ?? '',
    sip_display_name: sip?.displayName ?? '',
    sip_stun_server: sip?.stunServer ?? '',
    sip_dial_prefix: sip?.dialPrefix ?? '',
  };
}

export function usePracticeSession(params: URLSearchParams): PracticeSession {
  const [status, setStatus] = useState<SessieStatus>({ fase: 'bezig' });
  const [practice, setPractice] = useState<PraktijkProfiel | null>(null);
  const [practiceUserId, setPracticeUserId] = useState<string | null>(null);
  const [sip, setSip] = useState<SipInstellingen | null>(null);
  const [settings, setSettings] = useState<PracticeSettings>(DEFAULT_PRACTICE_SETTINGS);
  const [practiceIsNew, setPracticeIsNew] = useState(false);
  const [practiceNeedsName, setPracticeNeedsName] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  /**
   * De tokens die het HIS meestuurde, alleen tijdens de eerste uitwisseling nodig.
   * In een ref en niet in state: ze horen geen render te veroorzaken, en ze horen
   * ook niet in een dependency-lijst die de uitwisseling opnieuw kan aftrappen.
   */
  const accessToken = params.get('token');
  const externalUserId = params.get('user_id');
  const appTokenUitUrl = params.get('app_token');
  const alGewisseld = useRef(false);

  const verwerk = useCallback((antwoord: SessieAntwoord) => {
    setPractice(antwoord.practice);
    setPracticeUserId(antwoord.user?.id ?? null);
    setSip(antwoord.sip);
    setSettings(naarSettings(antwoord));
    setPracticeIsNew(antwoord.practiceIsNew);
    setPracticeNeedsName(antwoord.practiceNeedsName);
    // Een nieuwe gebruiker, of een die de wizard nooit afmaakte: zonder
    // display_name is het profiel niet ingevuld.
    setNeedsOnboarding(antwoord.userIsNew || (antwoord.user !== null && !antwoord.user.displayName));
    setStatus({ fase: 'ok' });

    // Het HIS kan een venstergrootte opgeven. Alleen zinvol in een popup.
    const { recordWindowWidth: w, recordWindowHeight: h } = antwoord.practice;
    if (w && h) {
      try {
        window.resizeTo(w, h);
      } catch {
        /* geen popup */
      }
    }
  }, []);

  const wissel = useCallback(
    async (appToken: string) => {
      if (!accessToken) {
        setStatus({ fase: 'geen-toegang', melding: 'Er staat geen token in de URL.' });
        return;
      }

      setStatus({ fase: 'bezig' });

      try {
        const antwoord = await api.post<SessieAntwoord>('/session', {
          appToken,
          accessToken,
          ...(externalUserId ? { externalUserId } : {}),
        });
        verwerk(antwoord);
      } catch (fout) {
        if (fout instanceof ApiFout) {
          // 403 betekent dat de tokens klopten maar de praktijk of gebruiker
          // gedeactiveerd is. Dat is iets anders dan een ongeldig token, en de
          // gebruiker heeft er een andere boodschap aan.
          if (fout.status === 403) {
            setStatus({ fase: 'geblokkeerd', melding: fout.message });
            return;
          }
          setStatus({ fase: 'geen-toegang', melding: fout.message });
          return;
        }
        setStatus({
          fase: 'geen-toegang',
          melding: 'Geen verbinding met de server. Probeer het opnieuw.',
        });
      }
    },
    [accessToken, externalUserId, verwerk],
  );

  /**
   * Verdergaan op het cookie, zonder tokens in de URL.
   *
   * Een lege querystring is geen afgewezen gebruiker. Wie vanuit dit scherm naar
   * /settings of /handleiding gaat en daar op terug drukt, komt terug op `/` zonder
   * query-parameters -- die staan daar met opzet niet meer in, want ze horen niet in
   * de browsergeschiedenis. Ook een herlaadactie op een schoongemaakte URL komt hier
   * uit. De sessie zit in dat geval gewoon in het HttpOnly-cookie, en GET /me geeft
   * hetzelfde antwoord als de tokenruil.
   */
  const hervat = useCallback(async () => {
    setStatus({ fase: 'bezig' });

    try {
      verwerk(await api.get<SessieAntwoord>('/me'));
    } catch (fout) {
      if (fout instanceof ApiFout && fout.status === 403) {
        setStatus({ fase: 'geblokkeerd', melding: fout.message });
        return;
      }
      // Ook bij een 401: er is geen sessie om op verder te gaan, en zonder token in
      // de URL is er ook niets om er een op te zetten.
      setStatus({ fase: 'geen-toegang', melding: 'Er staat geen token in de URL.' });
    }
  }, [verwerk]);

  useEffect(() => {
    if (alGewisseld.current) return;
    alGewisseld.current = true;

    if (!accessToken) {
      void hervat();
      return;
    }

    if (!appTokenUitUrl) {
      // Zonder app_token kan de uitwisseling niet; vraag erom in plaats van "geen
      // toegang" te tonen, want dit is een configuratiefout in het HIS en niet een
      // afgewezen gebruiker.
      setStatus({ fase: 'app-token-nodig' });
      return;
    }

    void wissel(appTokenUitUrl);
  }, [accessToken, appTokenUitUrl, hervat, wissel]);

  const herlaad = useCallback(async () => {
    try {
      const antwoord = await api.get<SessieAntwoord>('/me');
      verwerk(antwoord);
    } catch (fout) {
      if (fout instanceof ApiFout && fout.status === 401) {
        setStatus({ fase: 'geen-toegang', melding: 'De sessie is verlopen.' });
      }
      // Andere fouten laten de bestaande gegevens staan: een mislukte verversing is
      // geen reden om een lopend consult af te breken.
    }
  }, [verwerk]);

  const pasSettingsAan = useCallback((deel: Partial<PracticeSettings>) => {
    setSettings((vorige) => ({ ...vorige, ...deel }));
  }, []);

  return {
    status,
    practice,
    practiceUserId,
    sip,
    settings,
    practiceIsNew,
    practiceNeedsName,
    needsOnboarding,
    probeerAppToken: wissel,
    herlaad,
    pasSettingsAan,
    markeerOnboardingKlaar: useCallback(() => setNeedsOnboarding(false), []),
    markeerPraktijkNaam: useCallback((naam: string) => {
      setPractice((vorige) => (vorige ? { ...vorige, practiceName: naam } : vorige));
      setPracticeIsNew(false);
      setPracticeNeedsName(false);
    }, []),
  };
}
